import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { backend_url } from '../../App';

const Faq = () => {
  const [faqs, setFaqs] = useState([]);
  const [openIndex, setOpenIndex] = useState(null);

  useEffect(() => {
    fetchFaqs();
  }, []);

  const fetchFaqs = async () => {
    try {
      const response = await axios.get(`${backend_url}/api/faq`);
      setFaqs(response.data);
    } catch (error) {
      console.error('Error fetching FAQs:', error);
    }
  };

  const toggleFaq = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div
      className="relative overflow-hidden px-4 sm:px-8 py-12"
      style={{ background: "linear-gradient(180deg, #ffffff 0%, #F6F1FE 100%)" }}
    >
      {/* Faint dot grid */}
      <div
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage: "radial-gradient(circle, #8353C6 1px, transparent 1px)",
          backgroundSize: "24px 24px",
        }}
      />

      <div className="relative max-w-3xl mx-auto">
        {/* Header */}
        <div className="flex flex-col items-center mb-10 gap-2">
          <span
            className="text-white text-xs font-bold px-4 py-1 rounded-full"
            style={{ background: "linear-gradient(90deg, #8353C6, #9E79D2)" }}
          >
            💡 Got Questions?
          </span>
          <h2
            className="text-2xl sm:text-3xl md:text-4xl font-semibold text-gray-900 text-center"
            style={{ fontFamily: "'Times New Roman', Serif" }}
          >
            Frequently Asked Questions
          </h2>
          <div className="flex items-center gap-3">
            <div className="h-px w-12 bg-gradient-to-r from-transparent to-purple-400" />
            <div className="w-1.5 h-1.5 rotate-45 bg-purple-500" />
            <div className="h-px w-12 bg-gradient-to-l from-transparent to-purple-400" />
          </div>
        </div>

        {/* FAQ Accordion */}
        <div className="flex flex-col gap-3">
          {faqs.map((faq, index) => {
            const isOpen = openIndex === index;
            return (
              <div
                key={faq._id}
                className={`rounded-2xl border-2 overflow-hidden transition-all duration-300
                  ${isOpen
                    ? "border-purple-400 shadow-[0_4px_20px_rgba(124,58,237,0.15)]"
                    : "border-purple-100 hover:border-purple-300"
                  } bg-white`}
              >
                {/* Question */}
                <button
                  className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left"
                  onClick={() => toggleFaq(index)}
                >
                  <div className="flex items-center gap-3">
                    {faq.icon_image && (
                      <div className="w-9 h-9 rounded-xl bg-purple-50 flex items-center justify-center flex-shrink-0 overflow-hidden">
                        <img
                          crossOrigin="anonymous"
                          src={`${faq.icon_image}`}
                          alt="icon"
                          className="w-6 h-6 object-contain"
                        />
                      </div>
                    )}
                    <span className={`text-sm sm:text-base font-semibold transition-colors ${isOpen ? "text-purple-700" : "text-gray-800"}`}>
                      {faq.question}
                    </span>
                  </div>
                  <div
                    className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-300
                      ${isOpen ? "text-white rotate-45" : "text-purple-500 rotate-0"}`}
                    style={isOpen ? { background: "linear-gradient(135deg, #8353C6, #9E79D2)" } : { background: "#F6F1FE" }}
                  >
                    <span className="text-lg font-bold leading-none">+</span>
                  </div>
                </button>

                {/* Answer */}
                <div
                  className={`transition-all duration-300 ease-in-out overflow-hidden
                    ${isOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"}`}
                >
                  <div className="px-5 pb-5 pt-1">
                    <div className="h-px bg-purple-100 mb-4" />
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}

          {faqs.length === 0 && (
            <div className="text-center py-12 text-gray-400">
              <p className="text-4xl mb-3">❓</p>
              <p>No FAQs available yet.</p>
            </div>
          )}
        </div>

        {/* Bottom CTA */}
        <div className="mt-10 text-center rounded-2xl border border-purple-100 bg-white p-6 shadow-sm">
          <p className="text-gray-600 text-sm mb-3">Still have questions? We're here to help.</p>
          <a
            href="https://wa.me/919944592904?text=Hi%2C%20I%20have%20a%20question%20about%20your%20products"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl text-white text-sm font-semibold shadow hover:opacity-90 transition"
            style={{ background: "linear-gradient(90deg, #16a34a, #22c55e)" }}
          >
            <svg className="w-4 h-4 fill-white" viewBox="0 0 24 24">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
              <path d="M12 0C5.373 0 0 5.373 0 12c0 2.138.564 4.14 1.541 5.875L0 24l6.299-1.519A11.956 11.956 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.86 0-3.607-.487-5.12-1.34L2 22l1.372-4.768A9.942 9.942 0 012 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z"/>
            </svg>
            Chat on WhatsApp
          </a>
        </div>
      </div>
    </div>
  );
};

export default Faq;
